/**
 * Loyal Review — Administrator Guide (English)
 *
 * This document is user-facing. It is rendered inside the in-app
 * documentation viewer and is intended for merchants configuring
 * the Loyal Review flow, floor managers redeeming gift vouchers,
 * and support staff answering guest questions.
 */

export default `# Loyal Review — Administrator Guide

## What is Loyal Review?

Loyal Review is an alternate positive-feedback path inside Review Shield.
Guests who rate the experience above the Public Review Threshold see a
gentle choice screen:

- **Receive a Gift from Us** — a voucher is emailed to the guest and
  becomes redeemable on their next visit.
- **Continue to Leave a Public Review** — the existing Google review
  flow, byte-for-byte unchanged.

Loyal Review reuses the existing Review Shield UI, the existing Voucher
Engine, and the existing email pipeline. There is no separate voucher
workflow, no new admin dashboard, no new report.

---

## Merchant workflow

### 1. Create a voucher (existing flow)
Go to **Vouchers → Allocate**. Add recipients, values, and delivery
method exactly as you do today.

### 2. Mark the voucher "Available for Loyal Review"
Before clicking **Add to List**, tick the new
"Available for Loyal Review" checkbox. This flags the voucher
document as a *reward blueprint* that the Review Shield flow may
clone from. The blueprint itself is never redeemed — it exists so
you can review, edit, or archive the reward in the standard Voucher
list.

### 3. Enable Loyal Review on the card
Go to **Cards → Card Settings → Review Shield**. Below the Public
Review Threshold you now see a **Positive Experience** toggle with two
options: Standard or Loyal Review.

- Choose **Standard** to keep today's behaviour (positive raters go
  straight to the public review flow).
- Choose **Loyal Review** and pick a reward from the dropdown. Only
  vouchers you marked "Available for Loyal Review" appear here.

### 4. Save

The card immediately switches to the Loyal Review flow the next time a
guest taps it and rates at or above the threshold.

---

## Guest experience

1. Guest taps the card and rates the experience.
2. If the rating is **at or above** the Public Review Threshold AND
   the card has Loyal Review enabled, the guest sees the choice
   screen.
3. If the guest picks **Receive a Gift**, they:
   - Enter their email
   - Read and accept the Voucher Terms & Conditions (mandatory)
   - Receive their voucher on-screen and by email
   - Are gently invited to leave a public review afterwards
4. If the guest picks **Continue to Leave a Public Review**, the
   flow is identical to today's Standard behaviour.

The guest never sees any Loyal Review branding. They see hospitality
copy: *"Choose one option: Receive a Gift from Us · Continue to Leave
a Public Review."*

---

## Return visit rule

Every voucher issued via Loyal Review is stamped with a
\`redeemable_from\` timestamp equal to **the start of the NEXT
calendar day in the organization's local timezone**. Staff who scan a
Loyal Review voucher on the day of issuance will see the
\`NOT_YET_REDEEMABLE\` refusal message.

Manually issued vouchers are **not affected** — they remain
redeemable immediately. The return-visit rule applies only to
Loyal Review issuances.

---

## Positive Review Threshold — one setting, both paths

The Public Review Threshold you already configured for Review Shield
is the *single source of truth*. Loyal Review inherits it
automatically. There is no separate threshold, no duplicate setting,
no risk of the two flows drifting apart.

---

## Voucher Terms & Conditions

Before a Loyal Review voucher can be issued, the guest MUST
explicitly accept the Voucher Terms & Conditions on the email
screen. The acceptance is captured with:

- Timestamp
- Language displayed (en / fr / es)
- Terms version (\`v1.0\` at ship)
- Source = \`LR\`

If the checkbox is not ticked:

- No voucher is issued.
- No email is sent.
- Nothing is persisted beyond the request-level audit log.

---

## Reminder email

If a guest has not redeemed their voucher by the midpoint of its
lifetime, an automatic reminder email is sent — exactly once — via
the existing Resend pipeline.

---

## Notes for support staff

- Vouchers issued via Loyal Review show a **LR** source in the
  Voucher Reports and Voucher Audit views. Filter by this source to
  count Loyal Review issuances.
- If a guest reports "the voucher won't scan on the day I got it,"
  confirm with the return-visit rule above — this is by design.
- If a merchant asks "how do I turn Loyal Review off," they can
  either flip the card back to **Standard** in Card Settings OR
  archive/deactivate the reward voucher (both stop new issuances).
`;
